/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
  COLUMN_NAMES as DUEOUT_COLUMN_NAMES,
  DueOut,
  DueOutFetchArgs,
  getDueOutsViews,
} from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import { twoDigits } from '../../assets/ts/formatting/formatting'
import { scrollToTop } from '../../assets/ts/general/general'
import ExportToCSVButton from '../../components/buttons/export-to-csv-btn'
import Filters from '../../components/filters/filters'
import KPIs from '../../components/kpi/kpis'
import PageOverlayBusy from '../../components/page-overlay/page-overlay-busy'
import DueoutsTable from '../../components/table/dueouts-table'
import Tabs from '../../components/tabs/tabs'
import useDueOutCount from '../../hooks/dueout/use-dueout-count'
import useDueOutDistinctValues from '../../hooks/dueout/use-dueout-distinct-values'
import useDueoutViews from '../../hooks/dueout/use-dueout-view'
import usePageAccumulator from '../../hooks/usePageAccumulator'
import PageHeader from '../../layouts/header/page-header'
import { PreviousPageContext } from '../../store/previous-page-provider'
import { RoleContext } from '../../store/role-context-provider'
import { SelectionContext } from '../../store/selection-context-provider'
import {
  ALL_SELECT_VALUE,
  ALL_SELECT_VALUE_STRING,
  DUEOUT_INITIAL_FILTER,
} from '../../utils/constants/global-constants'

const DueOutsList = () => {
  const location = useLocation()
  const dueFilter = location.state

  const selection = useContext(SelectionContext)
  const { initialInquiryAndDueoutFilter } = useContext(RoleContext)
  const { currentPage, kpiClicked, setCurrentPage, setPreviousPage } =
    useContext(PreviousPageContext)
  const [KPIFilter, setKPIFilter] = useState<DueOutFetchArgs>({})
  const [pageNum, setPageNum] = useState(0)
  const [exportFetchIsBusy, setExportFetchIsBusy] = useState(false)
  const dueOutPaging: DueOutFetchArgs = {
    pageNumber: pageNum,
  }
  const UserRoleCodes: Partial<DueOutFetchArgs> = {
    userLoginRole: {
      CSSPASCODEFilter: initialInquiryAndDueoutFilter?.cssPASCODES,
      usersAssignedComponentCode: initialInquiryAndDueoutFilter?.assignedComponentCodeFilter,
      usersMajcomCodeFilter: initialInquiryAndDueoutFilter?.majcomCodeFilter,
      usersSectionCodeFilter: initialInquiryAndDueoutFilter?.sectionCodeFilter,
      usersServicingOrganizationFilter: initialInquiryAndDueoutFilter?.servicingOrganizationFilter,
      usersServicingOrganizationWithoutSectionFilter:
        initialInquiryAndDueoutFilter?.servicingOrganizationWithoutSectionFilter,
    },
  }
  const initialKpiFilters: DueOutFetchArgs = {
    dueDateLessThanDateIgnoreTime: undefined,
    isArchivedFilter: false,
    isViewApplied: true,
    statusCodesFilter: undefined,
    statusCodesOmittedFilter: [STATUS_CODE.SAVED],
  }

  const [dueoutFilter, setDueoutFilter] = useState<DueOutFetchArgs>({
    ...DUEOUT_INITIAL_FILTER,
    ...initialKpiFilters,
    ...UserRoleCodes,
    hasClosedAndCanceled: false,
  })
  useEffect(() => {
    setDueoutFilter((prevFilter) => ({
      ...prevFilter,
      ...UserRoleCodes,
    }))
  }, [initialInquiryAndDueoutFilter])
  useEffect(() => {
    if (dueFilter?.dueFilter) {
      setDueoutFilter((prevFilter) => ({
        ...prevFilter,
        // ...UserRoleCodes,
        ...dueFilter.dueFilter,
      }))
    }
  }, [dueFilter])

  const { dueouts, dueoutsAreLoading } = useDueoutViews({
    ...dueoutFilter,
    ...dueOutPaging,
  })

  const {
    dueOutCountIsLoading: kpiFilteredDueOutCountIsLoading,
    dueOutCountValue: kpiFilteredDueOutCount,
  } = useDueOutCount({
    ...dueoutFilter,
  })

  // When determining the allowed values for the dashboard filters,
  // consider only entries for the current user.  Also use the same value
  // of "isArchived" from the overall list filter
  const { dueOutDistinctValues, dueOutDistinctValuesAreLoading } = useDueOutDistinctValues({
    ...UserRoleCodes,
    assignedComponentCodeFilter: dueoutFilter.assignedComponentCodeFilter,
    categoryCodeFilter: dueoutFilter.categoryCodeFilter as number,
    distinctFiltersToQuery: [
      DUEOUT_COLUMN_NAMES.FY,
      DUEOUT_COLUMN_NAMES.AssignedComponentCode,
      DUEOUT_COLUMN_NAMES.ServicingMAJCOMCode,
      DUEOUT_COLUMN_NAMES.ServicingOrganization,
      DUEOUT_COLUMN_NAMES.ServicingBy,
      DUEOUT_COLUMN_NAMES.SectionCode,
      DUEOUT_COLUMN_NAMES.CategoryCode,
      DUEOUT_COLUMN_NAMES.SubCategoryCode,
      DUEOUT_COLUMN_NAMES.Priority,
    ],
    isArchivedFilter: false,
    isViewApplied: true,
    relatedUserFilter: dueoutFilter.relatedUserFilter,
    savedFilter: false,
    sectionCodeFilter: dueoutFilter.sectionCodeFilter,
    servicingByFilter: dueoutFilter.servicingByFilter,
    servicingMAJCOMCodeFilter: dueoutFilter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: dueoutFilter.servicingOrganizationFilter,
    subCategoryCodeFilter: dueoutFilter.subCategoryCodeFilter as number,
    yearFilter: dueoutFilter.yearFilter,
  })

  const recordsToExport = dueouts.filter((d) => selection.keyIsSelected(d.DueOutGUID ?? ''))

  const kpiFilteredDueOutCountString = kpiFilteredDueOutCount.toLocaleString()

  const accumulatedDueOuts = usePageAccumulator(pageNum, dueouts)

  useEffect(() => {
    selection.mergeWithAllLoadedRecordKeys(dueouts.map((i) => i.DueOutGUID ?? ''))
  }, [dueouts])

  const handleBottomScrollReached = () => {
    if (kpiFilteredDueOutCount > accumulatedDueOuts.length) {
      setPageNum((num) => num + 1)
    }
  }

  const tableitems = [
    {
      content: (
        <DueoutsTable
          count={twoDigits(kpiFilteredDueOutCountString)}
          dueOuts={accumulatedDueOuts}
          onBottomScrollReached={handleBottomScrollReached}
          title='Total Due Outs'
        />
      ),
      count: twoDigits(kpiFilteredDueOutCountString),
      isActive: true,
      tabTitle: 'Due outs',
    },
  ]
  const busy =
    dueOutDistinctValuesAreLoading ||
    dueoutsAreLoading ||
    kpiFilteredDueOutCountIsLoading ||
    exportFetchIsBusy

  const handleClearAll = () => {
    setDueoutFilter({
      ...DUEOUT_INITIAL_FILTER,
      ...initialKpiFilters,
      ...UserRoleCodes,
    })
  }
  const getSearch = (event: { target: { value: string } }): void => {
    const searchWord: string = event.target.value.toLocaleLowerCase()
    setDueoutFilter((prev) => ({ ...prev, searchFilter: searchWord }))
  }
  const resetFilterOptions = (priorArgs, order, updatedArgs) => ({
    ...priorArgs,
    assignedComponentCodeFilter:
      order && order >= 2 ? priorArgs.assignedComponentCodeFilter : ALL_SELECT_VALUE,
    categoryCodeFilter: order && order >= 9 ? priorArgs.categoryCodeFilter : ALL_SELECT_VALUE,
    searchFilter: '',
    sectionCodeFilter: order && order >= 8 ? priorArgs.sectionCodeFilter : ALL_SELECT_VALUE,
    servicingByFilter: order && order >= 4 ? priorArgs.servicingByFilter : ALL_SELECT_VALUE_STRING,
    servicingMAJCOMCodeFilter:
      order && order >= 3 ? priorArgs.servicingMAJCOMCodeFilter : ALL_SELECT_VALUE,
    servicingOrganizationFilter:
      order && order >= 5 ? priorArgs.servicingOrganizationFilter : ALL_SELECT_VALUE_STRING,
    subCategoryCodeFilter:
      order && order >= 10 ? priorArgs.subCategoryCodeFilter : ALL_SELECT_VALUE,
    yearFilter: order && order >= 1 ? priorArgs.yearFilter : ALL_SELECT_VALUE,
    ...updatedArgs,
  })
  const updateDueoutFilter = (updatedArgs, order) => {
    setDueoutFilter((priorArgs) => resetFilterOptions(priorArgs, order, updatedArgs))
  }
  const handleDashboardFilterChange = (e, order) => {
    selection.reset()
    if (updateDueoutFilter) {
      setPageNum(0)
      updateDueoutFilter(e, order)
    }
  }
  const mainRef = useRef<any>(null)
  useEffect(() => {
    mainRef.current?.scrollIntoView()
  }, [mainRef?.current])

  async function getAllSelectedRecords(): Promise<DueOut[]> {
    const visibleSelectedRecords = accumulatedDueOuts.filter((d) =>
      selection.keyIsSelected(d.DueOutGUID ?? '')
    )

    const unloadedSelectedRecords = await getAllUnloadedSelectedRecords()

    const allSelectedRecords = [...visibleSelectedRecords, ...unloadedSelectedRecords]

    return allSelectedRecords
  }

  async function getAllUnloadedSelectedRecords(): Promise<DueOut[]> {
    if (selection.unloadedRecordsAreSelected) {
      setExportFetchIsBusy(true)
      // Becuase the user clicked the Select All in the past, all records
      // matching the current dashboard and KPI filters that are in pages
      // past the currently loaded/visible ones need to be fetched
      const items = await getDueOutsViews({
        ...dueoutFilter,
        ...KPIFilter,
        onePageOnly: false,
        pageNumber: (dueOutPaging.pageNumber ?? 0) + 1,
      })
      setExportFetchIsBusy(false)
      return items
    } else {
      return []
    }
  }
  const updateKpiFilter = (updatedArgs) => {
    scrollToTop()
    selection.reset()
    setPageNum(0)
    setDueoutFilter((priorArgs) => ({
      ...priorArgs,
      ...initialKpiFilters,
      ...updatedArgs,
    }))
  }

  useEffect(() => {
    // This effect runs whenever the location changes (when the user navigates to a different route)
    if (currentPage !== location.pathname) {
      // User has navigated to a new page, so update the previous page
      setPreviousPage(currentPage) // Store the previous page
      setCurrentPage(location.pathname) // Update the current page
    }
  }, [location, currentPage]) // Runs whenever location or currentPage changes

  return (
    <div ref={mainRef}>
      {dueoutFilter.searchFilter === '' && busy && <PageOverlayBusy />}
      <PageHeader
        extras={<div></div>}
        icon='icon-dueoutsmenuicon font-16 me-1 color-primary'
        name='Due Outs '
      />
      <div className='container'>
        <div className='row'>
          <div className='col-sm-12 col-lg listview-table'>
            <div className='shadow card w-100 h-100'>
              <div className='card-body'>
                <Tabs
                  anchorclass='title-color2 font-12 poppins-semibold'
                  badge='badge bgcolor-primary font-12 border-radius3 px-2 py-1 whitetext'
                  className='title-color2 lg'
                  isButtons={ kpiFilteredDueOutCount > 0 ?
                    <ExportToCSVButton fetchDataFunc={getAllSelectedRecords} pageType='Dueouts' /> : ''
                  }
                  //coming here
                  items={tableitems}
                  tablistclassName='overflow-auto'
                />
              </div>
            </div>
          </div>
          <div className='col-sm-12 listview-filters order-first order-lg-0'>
            <div className='w-100 h-100 whitebg bordered1'>
              <div className='filter-section mb-3'>
                <Filters
                  availableFilterValues={dueOutDistinctValues[0]}
                  filter={dueoutFilter}
                  getSearch={getSearch}
                  onClearAll={() => {
                    setPageNum(0)
                    handleClearAll()
                    selection.reset()
                  }}
                  onUpdateFilter={handleDashboardFilterChange}
                  pageType='Dueouts'
                />
              </div>
              <div className='kpis-section mb-3'>
                <KPIs
                  filter={dueoutFilter}
                  initialKpiFilters={initialKpiFilters}
                  pageType={'DUE OUTS'}
                  updateKpiFilter={updateKpiFilter}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default DueOutsList
